import { createForm, FORM_ERROR } from 'final-form';

import { apiClient, ApiError, genRecaptchaToken, getSettings } from '../../api';
import { toUrlEncoded } from '../../api/utils';
import { _apipe, _is, _when } from '../../utils';
import { createFocusDecorator } from '../../utils/decorators';
import { openModal } from '../modal/modal';

import { createLoadingDecorator } from './decorators';
import { registerField } from './field';
import getBooksModalContent from './getBooksModal';
import { validateForm } from './validate';

import './books-for-email.css';

/**
 * @typedef BFEState
 */

const BFEState = {
  captcha_token: undefined,
  recaptcha_site_key: undefined,
  errors: {},
};

export function getBooksByEmail(state) {
  const { email, captcha_token, errors } = state;
  return apiClient({
    path: '/widgets/tools/books-by-email',
    init: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;',
      },
      body: toUrlEncoded({
        email,
        captcha_token,
        captcha_token_type: 'recaptcha_v3_web',
      }),
    },
  }).then(response => {
    return response instanceof ApiError
      ? { ...state, errors: { ...errors, [response.name]: true } }
      : { ...state, books_success: true };
  });
}

export function formatErrors(state) {
  const { errors } = state;

  const errorsToFields = {
    invalid_email: 'email',
    error: 'email',
    incorrect_email: 'email',
    requests_flood_1: 'email',
  };

  const errorsTexts = {
    invalid_email: 'Please enter a valid email',
    error: 'This e-mail already exists in our database.',
    incorrect_email: 'Incorrect e-mail address.',
    requests_flood_1: 'Too many requests. Please try again in 5 minutes.',
  };

  const formError = Object.keys(errorsToFields).reduce(
    (acc, code) => (errors[code] ? code : acc),
    '',
  );

  const fieldsErrors = Object.keys(errors).reduce(
    (acc, code) =>
      errors[code]
        ? {
            ...acc,
            [errorsToFields[code]]: errorsTexts[code],
          }
        : acc,
    {},
  );

  return formError
    ? {
        [FORM_ERROR]: formError,
        ...fieldsErrors,
      }
    : undefined;
}

const successMessage = document.querySelector('.bfe__message-success');

const handleSuccess = () => {
  const formContainer = document.querySelector('.bfe-form__container');
  successMessage.classList.add('visible');
  openModal({
    id: 'books-modal',
    content: getBooksModalContent(),
    contentClasses: 'bg-blue-900 max-w-[728px]',
    closeButtonClasses: 'text-white hover:text-slate-50',
  });

  formContainer.remove();
};

/**
 * @param {FormValues} formValues
 * @param {FormApi} form
 * @param {Function} callback
 */
const onSubmit = formValues =>
  _apipe(
    _when(_is('email'), genRecaptchaToken, 'genRecaptchaToken'),
    _when(_is('email'), getBooksByEmail, 'getBooksByEmail'),
    _when(_is('books_success'), handleSuccess),
    formatErrors,
  )({
    ...BFEState,
    ...formValues,
    recaptcha_action: 'POST/widgets/tool/books_by_email',
  });

const form = createForm({
  onSubmit,
  initialValues: {
    email: '',
    name: 'bfe',
  },
  validate: validateForm,
});

createLoadingDecorator(form);
createFocusDecorator(form);

form.subscribe(() => {}, {
  hasSubmitErrors: true,
  submitting: true,
  submitError: true,
  submitErrors: true,
  submitFailed: true,
  submitSucceeded: true,
  values: true,
});

document.querySelector('form[name=bfe]').addEventListener('submit', event => {
  event.preventDefault();
  form.submit();
});

document.addEventListener('DOMContentLoaded', () => {
  [document.querySelector('input.bfe__input[name=email]')].forEach(htmlElement => {
    if (htmlElement.name) {
      registerField(htmlElement, form);
    }
  });
});

getSettings().then(response => {
  BFEState.recaptcha_site_key = response.body.env.recaptcha_site_key;
});
